import { z } from 'zod';
import quoteProgramOptionsJson from '@/data/quote-program-options.json';

/**
 * The grouped "Program Selection" options from the live site's Request a
 * Quote form (Gravity Form 3), verbatim — 7 groups, 159 programs.
 */
const groupSchema = z.object({ group: z.string(), options: z.array(z.string()) });

export const QUOTE_PROGRAM_GROUPS: readonly z.infer<typeof groupSchema>[] =
  z.array(groupSchema).parse(quoteProgramOptionsJson);

const allOptions = new Set(QUOTE_PROGRAM_GROUPS.flatMap((group) => group.options));

export function isQuoteProgramOption(value: string): boolean {
  return allOptions.has(value);
}

/**
 * Resolves a program title (e.g. from ?program= on a program page CTA) to the
 * exact option string in the quote dropdown; ® / punctuation differences are
 * normalised away. Returns undefined when there's no confident match.
 */
export function matchQuoteProgram(title: string | undefined): string | undefined {
  if (!title) return undefined;
  const norm = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');
  const target = norm(title);
  if (!target) return undefined;
  const all = QUOTE_PROGRAM_GROUPS.flatMap((group) => group.options);
  return (
    all.find((option) => norm(option) === target) ??
    all.find((option) => norm(option).startsWith(target) || target.startsWith(norm(option)))
  );
}
